import { useLazyGetMerchantActivitiesQuery } from 'mmfintech-backend-api'
import type { MerchantActivityResponse } from 'mmfintech-backend-api/types'
import { useCallback, useEffect, useState } from 'react'

export const NOTIFICATIONS_PAGE_SIZE = 10

// Pages are accumulated here, not in the RTK Query cache: marking an item as read invalidates
// the 'Activities' tag, and a refetched page must not be appended a second time.
export const useNotificationsList = () => {
  const [load] = useLazyGetMerchantActivitiesQuery()

  const [items, setItems] = useState<MerchantActivityResponse[]>([])
  const [page, setPage] = useState(-1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  const fetchPage = useCallback(
    async (nextPage: number) => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await load({ page: nextPage, size: NOTIFICATIONS_PAGE_SIZE }).unwrap()
        setItems(previous => (nextPage === 0 ? (response.content ?? []) : [...previous, ...(response.content ?? [])]))
        setPage(nextPage)
        setTotalPages(response.totalPages ?? 0)
        setTotalElements(response.totalElements ?? 0)
      } catch (err) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    },
    [load]
  )

  useEffect(() => {
    void fetchPage(0)
  }, [fetchPage])

  const loadMore = useCallback(() => fetchPage(page + 1), [fetchPage, page])

  const setRead = useCallback((id: number, isRead: boolean) => {
    setItems(previous => previous.map(item => (item.id === id ? { ...item, isRead } : item)))
  }, [])

  const markAllRead = useCallback(() => {
    setItems(previous => previous.map(item => ({ ...item, isRead: true })))
  }, [])

  return {
    items,
    totalElements,
    hasMore: page + 1 < totalPages,
    isLoading,
    isInitialLoading: isLoading && page < 0,
    error,
    loadMore,
    setRead,
    markAllRead
  }
}
